import { useLocation } from 'react-router-dom';
import { useReadAuthUserIdWithQueryParamsLookup } from '../../hooks/useReadAuthUserIdWithQueryParamsLookup';
import { useUserPublicInfo } from '../../api/public/useUserPublicInfo';
import { CareModel, MeetingTypeId } from '../../api/types';
import { useAuth } from '../../hooks';
import { useUserMeetings } from '../../api/scheduleAppointment/useUserMeetings';
import { PmStatus } from '../../models/meeting';

export const useThankYouPage = () => {
  const { userId } = useReadAuthUserIdWithQueryParamsLookup();
  const { data: userPublicData, isLoading: userLoading } =
    useUserPublicInfo(userId);
  const { isAuthenticated, loading } = useAuth();
  const { state } = useLocation();
  const { userEmail, bookedMeeting: bookedMeetingFromState } = state || {};

  const isCollaborative = userPublicData?.care_model === CareModel.Collaborative;

  // Authenticated users can land here directly (e.g. via a magic-link
  // login redirect in PublicRoute) without ever having passed a
  // bookedMeeting through router state, so fetch it ourselves in that case.
  const { data: userMeetings, isLoading: meetingsLoading } = useUserMeetings({
    filter: 'upcoming',
    order_by: 'asc',
  });

  const { data: pastOccurredMeetings, isLoading: pastMeetingsLoading } =
    useUserMeetings({
      pm_status: PmStatus.occurred,
      order_by: 'asc',
    });

  // Only surface the upcoming meeting when it's the user's initial visit -
  // a follow-up meeting shouldn't be shown here as "the" booked appointment.
  const initialMeetingType = isCollaborative
    ? MeetingTypeId.InitialConsultation
    : MeetingTypeId.InitialCoachCall;

  const upcomingInitialMeeting = userMeetings?.find(
    (meeting) => meeting.meeting_type === initialMeetingType
  );

  const bookedMeeting =
    bookedMeetingFromState ??
    (isAuthenticated ? upcomingInitialMeeting : undefined);

  if (!userId) return { redirectToHome: true };

  return {
    redirectToHome: false,
    timezone: userPublicData?.timezone,
    bookedMeeting,
    userEmail,
    isCollaborative,
    isLoading:
      loading ||
      userLoading ||
      (isAuthenticated && (meetingsLoading || pastMeetingsLoading)),
    hasOccurredMeeting: !!pastOccurredMeetings?.length,
  };
};
